// React
import React from "react";
import { useTranslation } from "react-i18next";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useSelector } from "react-redux";

// MUI
import {
    Box,
    TextField,
    Grid,
    InputAdornment,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    FormControl,
    MenuItem,
    InputLabel,
    Select,
} from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { styled } from "@mui/material/styles";

/** MUI icons */
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const PREFIX = "KiiperReporting";

const classes = {
    btnRoot: `${PREFIX}-btnRoot`,
    buttonContainer: `${PREFIX}-buttonContainer`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "#F8F9F8",
    marginTop: 16,
    padding: 30,
    borderRadius: 30,

    [`& .${classes.btnRoot}`]: {
        border: "1px solid #ff5968",
        borderRadius: 40,
        "&:hover": {
            backgroundColor: "#2f3190",
            border: "1px solid #2f3190",
        },
    },
    [`& .${classes.buttonContainer}`]: {
        display: "flex",
        justifyContent: "flex-end",
        padding: 15,
    },
}));

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)({
    backgroundColor: "#F8F9F8",
    boxShadow: "none",
    "&:before": {
        display: "none",
    },
});

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#031851" }} />} {...props} />
))({
    "& .MuiAccordionSummary-content": {
        margin: "12px 0",
    },
});

const AccordionDetails = styled(MuiAccordionDetails)({
    padding: "0px 0px 20px 0px",
    "& .MuiCard-root": {
        boxShadow: "none",
    },
});

export const KiiperReporting = (props) => {
    // Properties
    const { report, setReport, isError, isErrorFocus, setIsErrorFocus } = props;

    /** Internationalization i18n */
    const [t] = useTranslation("global");

    /** React redux */
    const todos = useSelector((state) => state.todos);

    /** Component states */

    /** Component functions */

    const handleChangeDiscount = (event) => {
        let discountAux = event.target.value >= 0 && event.target.value <= 100 ? event.target.value : report.discount;

        setReport({
            ...report,
            discount: discountAux,
            projectTotal: report.originalFee * (1 - discountAux / 100),
            isError: false,
        });
        setIsErrorFocus(false);
    };

    const handleChangeVersion = (event) => {
        setReport({
            ...report,
            version: event.target.value,
            isError: false,
        });
        setIsErrorFocus(false);
    };

    const handleChangeSinceDate = (date) => {
        setReport({
            ...report,
            sinceDateProposal: date,
            isError: false,
        });
        setIsErrorFocus(false);
    };

    return (
        <StyledContainer>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography
                        variant="h1"
                        color="primary"
                        sx={{
                            display: "inline-block",
                            fontWeight: 700,
                            pr: 6,
                        }}
                    >
                        Kiiper Reporting subscription
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Accordion
                        TransitionProps={{ unmountOnExit: true }}
                        sx={{
                            borderBottom: "3px solid #ff5968",
                        }}
                    >
                        <AccordionSummary aria-controls="panel-content" id="panel-header">
                            <Typography
                                variant="h2"
                                align="center"
                                sx={{
                                    fontWeight: 700,
                                }}
                            >
                                {t("invoicing.description")}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                <ListItem disableGutters alignItems="center">
                                    <ListItemIcon sx={{ minWidth: 35 }}>
                                        <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1">
                                                <span style={{ fontWeight: 600 }}>{t("proposals.intel")} </span>-{" "}
                                                {t("proposals.ïntelDet")}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                <ListItem disableGutters alignItems="center">
                                    <ListItemIcon sx={{ minWidth: 35 }}>
                                        <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1">
                                                <span style={{ fontWeight: 600 }}>{t("proposals.design")} </span>-{" "}
                                                {t("proposals.designDet")}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                <ListItem disableGutters alignItems="center">
                                    <ListItemIcon sx={{ minWidth: 35 }}>
                                        <CheckIcon fontSize="small" sx={{ color: "#031851" }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body1">
                                                <span style={{ fontWeight: 600 }}>{t("principal.settings")} </span>-{" "}
                                                {t("proposals.settingDet")}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            </List>
                        </AccordionDetails>
                    </Accordion>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <FormControl fullWidth required error={isError && report.version === ""}>
                        <InputLabel id="ver-simple-select-label">{t("services.version")}</InputLabel>
                        <Select
                            name="version"
                            labelId="ver-simple-select-label"
                            IconComponent={KeyboardArrowDownIcon}
                            value={report.version}
                            label={t("services.version")}
                            onChange={handleChangeVersion}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        mt: 0.7,
                                        borderRadius: 2,
                                        boxShadow: "10px 10px 15px 0px rgba(3,24,81,0.15)",
                                    },
                                },
                            }}
                            onClick={(e) => {
                                setIsErrorFocus(false);
                            }}
                            inputRef={(input) => {
                                input && isErrorFocus && isError && report.version === "" && input.focus();
                            }}
                        >
                            <MenuItem value={"Chile"}>Chile</MenuItem>
                            <MenuItem value={"Colombia"}>Colombia</MenuItem>
                            <MenuItem value={"República Dominicana"}>República Dominicana</MenuItem>
                            <MenuItem value={"Venezuela"}>Venezuela</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <TextField
                        fullWidth
                        label={t("miscellaneous.discount")}
                        value={report.discount}
                        name="discount"
                        onChange={handleChangeDiscount}
                        onBlur={() =>
                            setReport({
                                ...report,
                                discount: parseFloat(report.discount === "" ? 0 : report.discount).toFixed(2),
                            })
                        }
                        variant="outlined"
                        type="number"
                        InputProps={{
                            inputProps: {
                                min: 0,
                                max: 100,
                            },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        sx={{
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                                display: "none",
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                        adapterLocale={t("language.locale")}
                        localeText={{ clearButtonLabel: t("miscellaneous.clear") }}
                    >
                        <DatePicker
                            componentsProps={{
                                actionBar: { actions: ["clear"] },
                            }}
                            value={report.sinceDateProposal}
                            onChange={(newValue) => handleChangeSinceDate(newValue)}
                            inputFormat={todos.dateFormat}
                            renderInput={(params) => (
                                <TextField fullWidth required {...params} error={isError && !report.sinceDateProposal} />
                            )}
                            onClick={(e) => {
                                setIsErrorFocus(false);
                            }}
                            label={t("reports.initDate")}
                            views={["year", "month", "day"]}
                            inputRef={(input) => {
                                input && isErrorFocus && isError && !report.sinceDateProposal && input.focus();
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
            </Grid>
        </StyledContainer>
    );
};
